import {
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { AgGridAngular } from "ag-grid-angular";
import {
    ColDef,
    ColGroupDef,
    GridApi,
    GridOptions,
    RowDragEvent,
    themeBalham
} from "ag-grid-community";
import { Subject } from "rxjs";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { AgGridTableService } from "./ag-grid-table.service";
import { FooterColumnComponent } from "./footer-column/footer-column.component";
import { RaskColumn } from "./models/rask-column";

export type TableStyle = null | "structured-readonly";

@Component({
    selector: "rask-table",
    styleUrls: ["./table.component.scss"],
    templateUrl: "./table.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AgGridAngular,
        FooterColumnComponent
    ]
})

export class TableComponent implements OnInit, OnChanges {
    @ViewChild("agGrid", { read: ElementRef }) agGrid: ElementRef;
    @Input() columnDefs: (ColDef | ColGroupDef)[];
    @Input() columns?: RaskColumn[];
    @Input() components: any;
    @Input() customStyle: TableStyle;
    @Input() data: any[];
    @Input() editable = false;
    gridApi: GridApi;
    gridOptions: GridOptions;
    @Input() noScroll: boolean;
    rowData: any;
    @Output() rowDragEnd = new EventEmitter<{ data: any; startIndex: number; endIndex: number }>();
    @Input() showColumns = true;
    showSum = false;
    theme = themeBalham.withParams({
        inputIconColor: "transparent",
        fontSize: 14
    });

    private readonly agGridService = inject(AgGridTableService);

    private dragStartIndex: number;

    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("options") set list(val: GridOptions) {
        if (val !== undefined) {
            this.gridOptions = val;
        }
    }

    getStyleClass(): string {
        if (this.customStyle) {
            return `rask-tablestyle-${this.customStyle}`;
        }
        return "";
    }

    @HostListener("document:keydown", ["$event"]) keyup(e: KeyboardEvent) {
        if (!this.agGrid.nativeElement.contains(e.target)) {
            return undefined;
        }
        if (this.agGridService.handleKey(e, this.gridApi)) {
            e.preventDefault();
            e.stopImmediatePropagation();
            return false;
        }
        return undefined;
    }

    ngOnChanges(changes: SimpleChanges) {
        if (this.gridApi !== undefined && ObjectUtils.hasValue(changes.columns)) {
            this.setColumnVisible(changes.columns.currentValue);
        }
        this.refreshGrid();
    }

    ngOnInit() {
        this.setupColumns();
        if (this.gridOptions === undefined) {
            this.gridOptions = this.agGridService.getDefaultOptions(this.showColumns);
        }

        if (this.noScroll === true) {
            this.gridOptions.suppressHorizontalScroll = this.noScroll;
        }

        this.gridOptions.columnDefs = this.columnDefs;
        this.gridOptions.rowData = this.data;
        this.gridOptions.onRowDragEnter = (e: RowDragEvent) => {
            this.dragStartIndex = e.node.rowIndex;
        };
        this.gridOptions.onRowDragEnd = (e: RowDragEvent) => {
            this.rowDragEnd.emit({
                data: e.node.data,
                startIndex: this.dragStartIndex,
                endIndex: e.node.rowIndex
            });
        };
    }

    onGridReady(params) {
        this.gridApi = params.api;
        this.updateGridSize();
    }

    @HostListener("window:resize", ["$event"]) onResize(e: Event) {
        this.updateGridSize();
    }

    refreshGrid() {
        if (this.gridOptions !== undefined && this.gridApi) {
            const filter = this.gridApi.getFilterModel();
            this.gridOptions.rowData = this.data;
            this.gridApi.setGridOption("rowData", this.gridOptions.rowData);
            this.gridApi.setFilterModel(filter);
            this.updateGridSize();
        }
    }

    setColumnVisible(columns: any[]) {
        const cols = [];
        let val = false;
        columns.forEach(x => {
            if (ObjectUtils.hasValue(x.hide)) {
                val = !x.hide;
                cols.push(x.field);
            }
        });

        if (cols.length > 0) {
            this.gridApi.setColumnsVisible(cols, val);
        }
    }

    setupColumns() {
        if (this.columnDefs !== undefined) {
            return;
        }

        if (this.columns !== undefined) {
            this.columns.forEach(c => {
                if (!c.dataChanged) {
                    c.dataChanged = new Subject();
                }
            });
            this.columnDefs = this.agGridService.getColDefsFromRaskColumn(this.columns);
        } else {
            this.columnDefs = this.agGridService.getColDefsFromObject(this.data, this.editable);
        }
    }

    shouldShowSum = () => this.columns !== undefined && this.columns.find(r => r.footerAgg || r.footerText) != null;

    updateColdefs() {
        this.columnDefs = this.agGridService.getColDefsFromRaskColumn(this.columns);
        this.gridApi.setGridOption("columnDefs", this.columnDefs);
    }

    updateGridSize() {
        this.showSum = false;
        if (!this.gridApi) {
            return;
        }

        setTimeout(() => {
            this.agGridService.syncColumnsWidth(this.gridApi.getColumns(), this.columns);
            this.showSum = this.shouldShowSum();
            this.gridApi.sizeColumnsToFit();
        });
    }
}
